// Generated by Copilot

import { useCallback, useState } from "react";

import { Callout, Icon } from "@blueprintjs/core";
import { isAfter } from "date-fns";
import { useNavigate } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { OneOneSession } from "@/components/pages_logged_in/program/components/program-component/panels/one-one-coaching/OneOneSession";
import { StatusIndicator } from "@/components/pages_logged_in/program/components/program-component/panels/one-one-coaching/StatusIndicator";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { StyledButton } from "@/components/pieces/link/LinkButton";
import { ComponentStatus, MeetingStatus } from "@/graphql";
import {
  isParticipantComponent,
  PanelProps,
  ParticipantComponent,
} from "@/types/programs";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

import { CoachSelectionAfterIntro } from "./CoachSelectionAfterIntro";

type ChildComponent = NonNullableArrayItem<
  ParticipantComponent["childComponents"]
>;

type Component = (ParticipantComponent | ChildComponent) & {
  parentComponent?: ParticipantComponent["parentComponent"];
  specialty?: ParticipantComponent["specialty"];
  meetings?: ParticipantComponent["meetings"];
  introSessionCount?: ParticipantComponent["introSessionCount"];
};

const _generateIconArray = (count: number) =>
  Array.from({ length: count }, (_, i) => i);

export function FindAnotherCoach({
  component,
  specialtySlug,
  parentComponentId,
}: {
  component: Component;
  specialtySlug?: string;
  parentComponentId?: string;
}) {
  const navigate = useNavigate();
  const { introSessionCount, meetings } = component;

  const totalActiveMeetings =
    meetings?.filter((meeting) => !meeting.canceledAt)?.length || 0;

  const allowFindOtherCoach =
    !!meetings?.length && totalActiveMeetings < (introSessionCount ?? 0);

  if (!allowFindOtherCoach) {
    return null;
  }

  return (
    <Flex className="items-center gap-2.5">
      <StyledButton
        className="px-4 py-2"
        onClick={() => {
          navigate(
            `/match/${specialtySlug}?programComponentId=${parentComponentId}`,
          );
        }}
      >
        <Box className="text-sm font-medium text-gray-800">
          Find Another Coach
        </Box>
      </StyledButton>
      <Box className="text-sm text-gray-700">
        Book by{" "}
        <Box className="inline-block text-sm font-semibold">
          {component?.targetEndDate &&
            formatDate(component.targetEndDate, "MMMM do")}
        </Box>
      </Box>
    </Flex>
  );
}

export function OneOneIntroItemPanel({ component, view = "list" }: PanelProps) {
  const [showCancelMeeting, setShowCancelMeeting] = useState(false);
  const {
    participantGuidance,
    targetEndDate,
    introSessionCount,
    meetings,
    parentComponent,
    closedAt,
  } = component as ParticipantComponent;

  const navigate = useNavigate();

  const currentDate = new Date();
  const isClosed =
    (targetEndDate && isAfter(currentDate, new Date(targetEndDate))) ||
    closedAt;
  const isCompleted = component?.status === ComponentStatus.Completed;

  const totalActiveMeetings =
    meetings?.filter((meeting) => !meeting.canceledAt)?.length || 0;

  const isShowPanelInfo = !isClosed && totalActiveMeetings === 0;

  const handleFindMyCoach = useCallback(() => {
    const programComponentId = parentComponent?.id;
    const specialtySlug = parentComponent?.specialty?.slug;
    navigate(
      `/match/${specialtySlug}?programComponentId=${programComponentId}`,
    );
  }, [parentComponent]);

  const onToggleChange = () => {
    setShowCancelMeeting(!showCancelMeeting);
  };

  const specialty = parentComponent?.specialty;

  const filteredMeetings = showCancelMeeting
    ? meetings
    : meetings?.filter((meeting) => !meeting.canceledAt);

  const hasCanceledMeetings = meetings?.some((meeting) => meeting.canceledAt);

  if (isParticipantComponent(component) && view === "feed") {
    const completedMeetings = filteredMeetings?.filter(
      (meeting) =>
        meeting.status === MeetingStatus.Complete &&
        meeting?.product?.introProduct === true,
    );

    const ongoingMeetings =
      filteredMeetings?.filter(
        (meeting) =>
          meeting.status && [MeetingStatus.Ongoing].includes(meeting.status),
      ) ?? [];

    const upcomingMeetings =
      filteredMeetings?.filter(
        (meeting) =>
          meeting.status &&
          [
            MeetingStatus.Scheduling,
            MeetingStatus.Upcoming,
            MeetingStatus.Soon,
          ].includes(meeting.status),
      ) ?? [];

    return completedMeetings && completedMeetings.length > 0 ? (
      <>
        <div className="round-corners bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8]">
          <CoachSelectionAfterIntro
            component={component}
            selectedCoach={component?.selectedCoach}
          />
        </div>
        {(ongoingMeetings.length > 0 || upcomingMeetings?.length > 0) && (
          <div className="xs:gap-2.5 flex flex-col md:gap-3">
            {[...ongoingMeetings, ...upcomingMeetings].map((meeting) => (
              <OneOneSession
                meeting={meeting}
                component={component}
                key={meeting?.id}
              />
            ))}
          </div>
        )}
      </>
    ) : (
      <>
        <Box className="round-corners xs:p-4 xs:gap-4 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-6 md:p-6">
          {isShowPanelInfo && (
            <>
              <div className="xs:gap-0.5 flex flex-col md:gap-1">
                <Box className="text-lg font-semibold text-blue-800">
                  Meet with coaches and find your match
                </Box>
                <Box className="xs:text-sm text-gray-700 md:text-base">
                  Book your {specialty?.name} Intro Sessions by{" "}
                  <strong>{formatDate(targetEndDate, "MMMM do")}</strong>
                </Box>
              </div>
              <div>
                <PrimaryButton
                  className="px-4 py-2"
                  onClick={handleFindMyCoach}
                >
                  Find My Coach
                </PrimaryButton>
              </div>
            </>
          )}
          {ongoingMeetings?.length > 0 && (
            <>
              <div className="text-lg font-semibold text-blue-800">
                You have{" "}
                {pluralize({
                  amount: ongoingMeetings.length,
                  singular: "a live coaching session",
                  plural: "live coaching sessions",
                  showCount: ongoingMeetings.length > 1,
                })}
              </div>

              <div className="xs:gap-2.5 flex flex-col md:gap-3">
                {ongoingMeetings.map((meeting) => (
                  <div key={meeting?.id} className="round-corners bg-white">
                    <OneOneSession meeting={meeting} component={component} />
                  </div>
                ))}
              </div>
            </>
          )}
          {!ongoingMeetings.length && upcomingMeetings?.length > 0 && (
            <>
              <div className="text-lg font-semibold text-blue-800">
                You have{" "}
                {pluralize({
                  amount: upcomingMeetings.length,
                  singular: "an upcoming coaching session",
                  plural: "upcoming coaching sessions",
                  showCount: upcomingMeetings.length > 1,
                })}
              </div>

              <div className="xs:gap-2.5 flex flex-col md:gap-3">
                {upcomingMeetings.map((meeting) => (
                  <div key={meeting?.id} className="round-corners bg-white">
                    <OneOneSession meeting={meeting} component={component} />
                  </div>
                ))}
              </div>
            </>
          )}
        </Box>
        {ongoingMeetings.length > 0 && upcomingMeetings?.length > 0 && (
          <div className="xs:gap-2.5 flex flex-col md:gap-3">
            {upcomingMeetings.map((meeting) => (
              <OneOneSession
                meeting={meeting}
                component={component}
                key={meeting?.id}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Box className="xs:gap-3.5 flex flex-col md:gap-4 empty:hidden">
      {isShowPanelInfo && (
        <Box className="round-corners xs:p-4 xs:gap-4 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-6 md:p-6">
          <div className="xs:gap-0.5 flex flex-col md:gap-1">
            <Box className="text-lg font-semibold text-blue-800">
              Meet with coaches and find your match
            </Box>
            <Box className="xs:text-sm text-gray-700 md:text-base">
              Book your {specialty?.name} Intro Sessions by{" "}
              <strong>{formatDate(targetEndDate, "MMMM do")}</strong>
            </Box>
          </div>
          <div>
            <PrimaryButton className="px-4 py-2" onClick={handleFindMyCoach}>
              Find My Coach
            </PrimaryButton>
          </div>
        </Box>
      )}

      {isParticipantComponent(component) ? (
        <div className="xs:gap-2.5 flex flex-col md:gap-3">
          {filteredMeetings?.map((meeting) => (
            <OneOneSession
              meeting={meeting}
              component={component}
              key={meeting?.id}
            />
          ))}
        </div>
      ) : null}

      {totalActiveMeetings > 0 && !isCompleted && (
        <FindAnotherCoach
          component={component}
          specialtySlug={parentComponent?.specialty?.slug}
          parentComponentId={parentComponent?.id}
        />
      )}

      {totalActiveMeetings == introSessionCount && (
        <Callout intent="success" icon={<Icon icon="tick" />}>
          <Box className="text-sm font-normal text-green-800">
            All available intro sessions booked
          </Box>
        </Callout>
      )}

      {participantGuidance && (
        <Box>
          <RichContentRenderer content={participantGuidance} />
        </Box>
      )}

      {view === "list" && (
        <div className="text-base text-gray-800">
          The objective of this component is for you to have the opportunity to
          meet with a few coaches and choose the right one. If you love your
          first coach, you don't have to do the other sessions. It's up to you.
        </div>
      )}

      <hr className="m-0 border-neutral-300" />

      <div className="flex flex-wrap items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1">
            {_generateIconArray(introSessionCount || 0).map((index) => (
              <StatusIndicator
                key={index}
                size={16}
                status={index < totalActiveMeetings ? "complete" : "notStarted"}
              />
            ))}
          </div>
          {pluralize({
            amount: introSessionCount,
            singular: "session",
            plural: "sessions",
          })}{" "}
          available
        </div>
        {hasCanceledMeetings && (
          <ToggleField
            checked={!showCancelMeeting}
            onChange={onToggleChange}
            padding={0}
            size={16}
            label="Hide Canceled Sessions"
          />
        )}
      </div>
    </Box>
  );
}
